import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { start, stop } from '../../../../actions/stream';
import { selectChannel } from '../../../../selectors/channelSelectors';
import { getFundamentalValue } from '../../../../utils/fundamentals';
import useActions from '../../../../utils/hooks/useActions';
import useFundamentals from '../../../../utils/hooks/useFundamentals';
import useLocalize from '../../../../utils/hooks/useLocalize';
import SectionHeaderComponent from '../../SectionHeaderComponent';
import '../SummarySection.scss';
import './CreditInfoSection.scss';
import {
  getDifference,
  getFieldValue,
  getLendingFeeRatio,
  getMarginValue,
  getWeeklyMarginRatio
} from './CreditInfoUtils';
import DailyMarginDate from './DailyMarginDate';
import LendingFeeDate from './LendingFeeDate';
import SectionRow from './SectionRow';
import WeeklyMarginDate from './WeeklyMarginDate';

const i18nKeyPrefix = 'symbol.details.margin.information.';

const CreditInfoSection = ({ symbol }) => {
  const [startStream] = useActions([start], []);
  const [stopStream] = useActions([stop], []);
  const fundamentals = useFundamentals(symbol);

  useEffect(() => {
    if (!symbol) {
      return;
    }

    startStream(`${symbol}/quote`);

    return () => {
      stopStream(`${symbol}/quote`);
    };
  }, [symbol, startStream, stopStream]);

  const channel = useMemo(() => `${symbol}/quote`, [symbol]);
  const quote = useSelector((state) => selectChannel(state, channel));
  const prevClose = quote?.prevClose;

  const localize = useLocalize();
  const title = localize('symbol.details.credit.info.section.title');

  const dailyOutstandingLoaned = getMarginValue(
    fundamentals,
    'dailyBorrowedSharesOutstanding'
  );
  const dailyNewLoaned = getMarginValue(fundamentals, 'dailyBorrowedSharesNew');
  const dailyRepaidLoaned = getMarginValue(
    fundamentals,
    'dailyBorrowedSharesRepaid'
  );
  const dailyLoanedChange = getDifference(dailyNewLoaned, dailyRepaidLoaned);

  const dailyOutstandingBorrowed = getMarginValue(
    fundamentals,
    'dailyLoanedSharesOutstanding'
  );

  const dailyNewBorrowed = getMarginValue(fundamentals, 'dailyLoanedSharesNew');
  const dailyRepaidBorrowed = getMarginValue(
    fundamentals,
    'dailyLoanedSharesRepaid'
  );
  const dailyBorrowedChange = getDifference(
    dailyNewBorrowed,
    dailyRepaidBorrowed
  );

  const dailyOutstandingSharesDifference = getDifference(
    dailyOutstandingLoaned,
    dailyOutstandingBorrowed
  );

  const dailyNewSharesDifference = getDifference(
    dailyNewLoaned,
    dailyNewBorrowed
  );

  const dailyRepaidSharesDifference = getDifference(
    dailyRepaidLoaned,
    dailyRepaidBorrowed
  );

  const dailySharesChangeDifference = getDifference(
    dailyLoanedChange,
    dailyBorrowedChange
  );

  let lendingFee = getFundamentalValue(fundamentals, 'lendingFee', 'value');

  if (typeof lendingFee === 'number') {
    lendingFee = lendingFee * 0.01;
  }

  const lendingFeeRatio = getLendingFeeRatio(prevClose, lendingFee);

  return (
    <div className="summary-section">
      <SectionHeaderComponent title={title} />
      <div className="summary-section-content">
        <div className="credit-info-grids-wrapper">
          <div className="credit-info-grid credit-info-grid-weekly-margin">
            <div className="credit-info-grid-label">
              {localize(i18nKeyPrefix + 'weeklyMarginBalance')}
            </div>
            <div className="credit-info-grids-container">
              <div className="credit-info-table">
                <div className="credit-info-table-body">
                  <div className="credit-info-table-row header-row">
                    <div className="credit-info-table-header-date">
                      <WeeklyMarginDate fundamentals={fundamentals} />
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'shareCount')}
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'changeFromPreviousWeek')}
                    </div>
                  </div>
                  <SectionRow
                    data={{
                      title: 'buyBalance',
                      values: [
                        {
                          value: getFieldValue(
                            fundamentals,
                            'weeklyMarginPurchases'
                          )
                        },
                        {
                          value: getFieldValue(
                            fundamentals,
                            'weeklyMarginPurchasesChange'
                          ),
                          signed: true
                        }
                      ]
                    }}
                    rowIndex={0}
                  />
                  <SectionRow
                    data={{
                      title: 'sellBalance',
                      values: [
                        {
                          value: getFieldValue(
                            fundamentals,
                            'weeklyMarginSales'
                          )
                        },
                        {
                          value: getFieldValue(
                            fundamentals,
                            'weeklyMarginSalesChange'
                          ),
                          signed: true
                        }
                      ]
                    }}
                    rowIndex={1}
                  />
                  <SectionRow
                    data={{
                      title: 'marginBuySellRatio',
                      values: [
                        {
                          value: getWeeklyMarginRatio(fundamentals),
                          precision: 2
                        },
                        {
                          hide: true
                        }
                      ]
                    }}
                    rowIndex={2}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="credit-info-grid credit-info-grid-daily-margin">
            <div className="credit-info-grid-label">
              {localize(i18nKeyPrefix + 'dailyMarginBalance')}
            </div>
            <div className="credit-info-grids-container">
              <div className="credit-info-table">
                <div className="credit-info-table-body">
                  <div className="credit-info-table-row header-row">
                    <div className="credit-info-table-header-date">
                      <DailyMarginDate fundamentals={fundamentals} />
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'shareCount')}
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'marginOpen')}
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'marginClose')}
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'changeFromPreviousDay')}
                    </div>
                  </div>
                  <SectionRow
                    data={{
                      title: 'financeBalance',
                      values: [
                        {
                          value: dailyOutstandingLoaned
                        },
                        {
                          value: dailyNewLoaned
                        },
                        {
                          value: dailyRepaidLoaned
                        },
                        {
                          value: dailyLoanedChange,
                          signed: true
                        }
                      ]
                    }}
                    rowIndex={0}
                  />
                  <SectionRow
                    data={{
                      title: 'stockLoanBalance',
                      values: [
                        {
                          value: dailyOutstandingBorrowed
                        },
                        {
                          value: dailyNewBorrowed
                        },
                        {
                          value: dailyRepaidBorrowed
                        },
                        {
                          value: dailyBorrowedChange,
                          signed: true
                        }
                      ]
                    }}
                    rowIndex={1}
                  />
                  <SectionRow
                    data={{
                      title: 'difference',
                      values: [
                        {
                          value: dailyOutstandingSharesDifference
                        },
                        {
                          value: dailyNewSharesDifference
                        },
                        {
                          value: dailyRepaidSharesDifference
                        },
                        {
                          value: dailySharesChangeDifference,
                          signed: true
                        }
                      ]
                    }}
                    rowIndex={2}
                  />
                  <SectionRow
                    data={{
                      title: 'marginTurnoverPeriod',
                      values: [
                        {
                          value: getMarginValue(
                            fundamentals,
                            'dailyTurnoverPeriod'
                          ),
                          precision: 1,
                          suffix: localize(i18nKeyPrefix + 'days')
                        },
                        {
                          hide: true
                        },
                        {
                          hide: true
                        },
                        {
                          hide: true
                        }
                      ]
                    }}
                    rowIndex={3}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="credit-info-grid credit-info-grid-lending-fee">
            <div className="credit-info-grid-label">
              {localize(i18nKeyPrefix + 'lendingFeeInformation')}
            </div>
            <div className="credit-info-grids-container">
              <div className="credit-info-table">
                <div className="credit-info-table-body">
                  <div className="credit-info-table-row header-row">
                    <div className="credit-info-table-header-date">
                      <LendingFeeDate fundamentals={fundamentals} />
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'lendingFee')}
                    </div>
                    <div className="credit-info-table-header">
                      {localize(i18nKeyPrefix + 'lendingFeeVsClose')}
                    </div>
                  </div>
                  <SectionRow
                    data={{
                      title: 'lendingFeeInformation',
                      values: [
                        {
                          value: lendingFee,
                          precision: 2,
                          suffix: localize(i18nKeyPrefix + 'yen'),
                          showSuffixIfNull: true
                        },
                        {
                          value: lendingFeeRatio,
                          precision: 3,
                          suffix: '%'
                        }
                      ]
                    }}
                    rowIndex={0}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditInfoSection;
